import { useEffect, useMemo, useState } from "react";
import type { HouseholdMember } from "../lib/familyLists";
import { FamilyBeaconTrigger } from "./FamilyBeacon";
import {
  checkInMapUrl,
  createFamilyCheckIn,
  loadFamilyCheckIns,
  type CheckInLocation,
  type CheckInStatus,
  type FamilyCheckInEntry
} from "../lib/familyCheckIns";

type Props = {
  householdId: string;
  currentUid: string;
  members: HouseholdMember[];
};

const STATUS_OPTIONS: Array<{ value: CheckInStatus; label: string }> = [
  { value: "made-it", label: "Made it" },
  { value: "heading-home", label: "Heading home" },
  { value: "running-late", label: "Running late" },
  { value: "all-good", label: "All good" }
];

const REQUEST_OPTIONS = ["Where are you?", "You good?", "What’s your ETA?"] as const;

const STATUS_LABELS: Record<CheckInStatus, string> = {
  "made-it": "Made it",
  "heading-home": "Heading home",
  "running-late": "Running late",
  "all-good": "All good",
  custom: "Checked in"
};

function relativeTime(milliseconds: number) {
  const seconds = Math.max(0, Math.floor((Date.now() - milliseconds) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(milliseconds).toLocaleDateString([], { month: "short", day: "numeric" });
}

export function FamilyCheckInCard({ householdId, currentUid, members }: Props) {
  const otherMembers = useMemo(() => members.filter(member => member.uid !== currentUid), [members, currentUid]);
  const [entries, setEntries] = useState<FamilyCheckInEntry[]>([]);
  const [mode, setMode] = useState<"status" | "note" | "request" | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [status, setStatus] = useState<CheckInStatus>("made-it");
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [location, setLocation] = useState<CheckInLocation | null>(null);
  const [locationLabel, setLocationLabel] = useState("");
  const [replyToId, setReplyToId] = useState("");
  const [busy, setBusy] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState(false);

  async function refresh() {
    if (!householdId) return;
    try {
      setEntries(await loadFamilyCheckIns(householdId));
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }

  useEffect(() => {
    refresh();
  }, [householdId]);

  function open(nextMode: "status" | "note" | "request", selectedRecipients: string[] = [], requestId = "") {
    setMode(nextMode);
    setStatus("made-it");
    setMessage("");
    setRecipients(selectedRecipients);
    setLocation(null);
    setLocationLabel("");
    setReplyToId(requestId);
    setNotice("");
  }

  function close() {
    if (!busy) setMode(null);
  }

  function toggleRecipient(uid: string) {
    setRecipients(selected => selected.includes(uid) ? selected.filter(value => value !== uid) : [...selected, uid]);
  }

  async function useCurrentLocation() {
    setLocationBusy(true);
    setNotice("");
    if (!navigator.geolocation) {
      setNotice("Location sharing is not supported on this device.");
      setLocationBusy(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(position => {
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        label: locationLabel.trim()
      });
      setLocationBusy(false);
    }, () => {
      setNotice("Your location was not shared. You can still type where you are.");
      setLocationBusy(false);
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 });
  }

  async function submit() {
    if (!mode || !recipients.length) return;
    setBusy(true);
    setNotice(mode === "request" ? "Sending request…" : "Checking in…");
    try {
      const result = await createFamilyCheckIn({
        householdId,
        action: mode,
        status: mode === "status" ? status : undefined,
        message,
        recipientUids: recipients,
        location: location ? { ...location, label: locationLabel.trim() } : null,
        replyToId: replyToId || undefined
      });
      setMode(null);
      setNotice(result.acceptedDevices
        ? `Saved for ${result.recipients}. Push accepted for ${result.acceptedDevices} device${result.acceptedDevices === 1 ? "" : "s"}; phone display is not confirmed.`
        : result.registeredDevices
          ? `Saved, but push failed on ${result.failedDevices || result.registeredDevices} registered device${result.registeredDevices === 1 ? "" : "s"}.${result.pushError ? ` Diagnostic: ${result.pushError}.` : ""}`
          : `Saved in FamDashFun, but the selected family members have no registered devices. They need to turn alerts on from their phones.`);
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "That check-in could not be sent.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <article className="home-card family-checkin-card" data-widget-id="family-ping">
        <div className="card-heading">
          <div><span>Stay connected, not tracked</span><h3>Family Ping</h3></div>
          <small>Shared only when you choose</small>
        </div>
        <p className="checkin-intro">Send a note, check in, or ask for a quick update—without background location tracking.</p>
        <div className="checkin-primary-actions">
          <button type="button" onClick={() => open("note")}>Ping</button>
          <button type="button" onClick={() => open("status")}>Check in</button>
          <button type="button" onClick={() => open("request")}>Ask where</button>
        </div>
        <div className="checkin-footer-actions">
          <button type="button" className="checkin-history-link" onClick={() => { setHistoryOpen(true); void refresh(); }}>History →</button>
          <FamilyBeaconTrigger householdId={householdId} />
        </div>
        {notice && !mode && <p className="checkin-notice" role="status">{notice}</p>}
      </article>

      {historyOpen && (
        <div className="checkin-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setHistoryOpen(false); }}>
          <section className="checkin-dialog checkin-history-dialog" role="dialog" aria-modal="true" aria-labelledby="checkin-history-title">
            <button type="button" className="checkin-close" aria-label="Close history" onClick={() => setHistoryOpen(false)}>×</button>
            <span className="checkin-eyebrow">The last seven days</span>
            <h2 id="checkin-history-title">Family Ping history</h2>
            <div className="checkin-feed" aria-label="Family Ping history">
              {entries.map(entry => (
                <div className="checkin-feed-item" key={entry.id}>
                  <span className={entry.action === "request" ? "request" : entry.action === "note" ? "note" : "status"} aria-hidden="true">{entry.action === "request" ? "?" : entry.action === "note" ? "✉" : "✓"}</span>
                  <div>
                    <strong>{entry.senderUid === currentUid ? "You" : entry.senderName}</strong>
                    <p>{entry.action === "request"
                      ? (entry.message || "Where are you?")
                      : entry.action === "note"
                        ? entry.message
                        : [entry.status ? STATUS_LABELS[entry.status] : "Checked in", entry.message, entry.location?.label].filter(Boolean).join(" · ")}</p>
                    <small>{relativeTime(entry.createdAt)}</small>
                  </div>
                  {entry.location && <a href={checkInMapUrl(entry.location)} target="_blank" rel="noreferrer">Map</a>}
                  {entry.action === "request" && entry.senderUid !== currentUid && (
                    <button type="button" className="checkin-reply" onClick={() => { setHistoryOpen(false); open("status", [entry.senderUid], entry.id); }}>Reply</button>
                  )}
                </div>
              ))}
              {!entries.length && !loadError && <p className="checkin-empty">No Family Pings yet. Start with a quick hello.</p>}
              {loadError && <button type="button" className="checkin-retry" onClick={refresh}>Couldn’t load Family Ping history. Try again.</button>}
            </div>
          </section>
        </div>
      )}

      {mode && (
        <div className="checkin-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
          <section className="checkin-dialog" role="dialog" aria-modal="true" aria-labelledby="checkin-dialog-title">
            <button type="button" className="checkin-close" aria-label="Close" onClick={close}>×</button>
            <span className="checkin-eyebrow">{mode === "status" ? "Share on your terms" : mode === "note" ? "Quick family note" : "Ask for a quick update"}</span>
            <h2 id="checkin-dialog-title">{mode === "status" ? "Check in" : mode === "note" ? "Send a ping" : "Where are you?"}</h2>

            {mode === "status" && (
              <div className="checkin-status-options" aria-label="Check-in type">
                {STATUS_OPTIONS.map(option => (
                  <button type="button" key={option.value} className={status === option.value ? "active" : ""} aria-pressed={status === option.value} onClick={() => setStatus(option.value)}>{option.label}</button>
                ))}
              </div>
            )}

            {mode === "request" && (
              <div className="checkin-status-options checkin-request-options" aria-label="Quick messages">
                {REQUEST_OPTIONS.map(option => (
                  <button type="button" key={option} className={message === option ? "active" : ""} aria-pressed={message === option} onClick={() => setMessage(option)}>{option}</button>
                ))}
              </div>
            )}

            <label className="checkin-field">
              <span>{mode === "status" ? "Add a note (optional)" : mode === "note" ? "Message" : "Message (optional)"}</span>
              <textarea value={message} onChange={event => setMessage(event.target.value)} maxLength={180} placeholder={mode === "status" ? "Made it to Union Station" : mode === "note" ? "Dinner is ready whenever you are." : "Hey, where are you at?"} />
            </label>

            {mode === "status" && (
              <div className="checkin-location-panel">
                <div>
                  <strong>Location is optional</strong>
                  <small>Nothing is shared until you tap the button and send.</small>
                </div>
                <label className="checkin-field">
                  <span>Place name (optional)</span>
                  <input value={locationLabel} onChange={event => setLocationLabel(event.target.value)} maxLength={100} placeholder="Union Station" />
                </label>
                <div className="checkin-location-actions">
                  <button type="button" className={location ? "active" : ""} disabled={locationBusy} onClick={useCurrentLocation}>{locationBusy ? "Getting location…" : location ? "Location added ✓" : "Share my location"}</button>
                  {location && <button type="button" className="secondary" onClick={() => setLocation(null)}>Remove</button>}
                </div>
              </div>
            )}

            <fieldset className="checkin-recipients">
              <legend>{mode === "request" ? "Ask" : "Send to"}</legend>
              <div>
                {otherMembers.map(member => (
                  <button type="button" key={member.uid} className={recipients.includes(member.uid) ? "active" : ""} aria-pressed={recipients.includes(member.uid)} onClick={() => toggleRecipient(member.uid)}>{member.firstName}</button>
                ))}
                {otherMembers.length > 1 && (
                  <button type="button" className={recipients.length === otherMembers.length ? "active" : ""} aria-pressed={recipients.length === otherMembers.length} onClick={() => setRecipients(recipients.length === otherMembers.length ? [] : otherMembers.map(member => member.uid))}>Everyone</button>
                )}
              </div>
            </fieldset>

            {notice && <p className="checkin-notice" role="status">{notice}</p>}
            <button type="button" className="checkin-submit" disabled={busy || !recipients.length || (mode === "note" && !message.trim()) || (mode === "status" && status === "custom" && !message.trim() && !location)} onClick={submit}>
              {busy ? "Sending…" : mode === "status" ? "Send check-in" : mode === "note" ? "Send ping" : "Send request"}
            </button>
            <p className="checkin-privacy">{mode === "note" ? "This note is sent only to the family members you select." : "FamDashFun never follows you in the background. Exact location is included only when you choose Share my location."}</p>
          </section>
        </div>
      )}
    </>
  );
}
